import { token } from '../../../gemeinsam/lib/api';
import { apiUrl } from '../../../gemeinsam/lib/plattform';

export type Anhang = { id: string; name: string; typ: string; groesse: number };

export const MAX_BYTES = 25 * 1024 * 1024;

/** Anzeigegröße einer Datei — 1,4 MB statt 1468006. */
export function groesseLesbar(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Bilder bekommen eine Vorschau, alles andere ein Symbol. */
export function istBild(typ: string): boolean {
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(typ);
}

/**
 * Lädt eine Datei hoch. Der Inhalt geht als roher Datenstrom zum Server, der
 * Name als Base64-Kopfzeile — so überstehen Umlaute und chinesische Zeichen
 * den Weg unbeschadet, was in einer HTTP-Kopfzeile sonst nicht gesichert ist.
 */
export async function dateiHochladen(datei: File): Promise<Anhang> {
  if (datei.size === 0) throw new Error('Die Datei ist leer.');
  if (datei.size > MAX_BYTES) {
    throw new Error(`Die Datei ist ${groesseLesbar(datei.size)} groß. Erlaubt sind 25 MB.`);
  }

  const antwort = await fetch(apiUrl('/api/dateien'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token() ?? ''}`,
      'Content-Type': datei.type || 'application/octet-stream',
      // btoa kann nur Latin-1 — der Umweg über die Bytefolge macht jeden
      // Dateinamen übertragbar.
      'X-Dateiname': btoa(String.fromCharCode(...new TextEncoder().encode(datei.name))),
    },
    body: datei,
  });

  const ergebnis = await antwort.json().catch(() => null);
  if (!antwort.ok) {
    throw new Error(ergebnis?.error || 'Die Datei konnte nicht hochgeladen werden.');
  }
  return ergebnis.data as Anhang;
}

/** Adresse der Datei auf dem Server — nur mit Anmeldekopfzeile erreichbar. */
export const anhangUrl = (id: string) => apiUrl(`/api/dateien/${id}`);

/**
 * Lädt eine Datei als Blob-Adresse für die Anzeige im Browser.
 *
 * Ein <img src="/api/dateien/…"> würde nicht funktionieren: der Browser
 * schickt bei Bildern keinen Authorization-Header, die Anfrage käme als 401
 * zurück und das Bild bliebe leer.
 */
export async function anhangAlsBlobUrl(id: string): Promise<string | null> {
  try {
    const antwort = await fetch(anhangUrl(id), {
      headers: { Authorization: `Bearer ${token() ?? ''}` },
    });
    if (!antwort.ok) return null;
    return URL.createObjectURL(await antwort.blob());
  } catch {
    return null;
  }
}

/**
 * Öffnet einen Anhang. Ein einfacher Link ginge nicht: die Datei liegt hinter
 * der Anmeldung und ein <a href> kann keinen Authorization-Header mitschicken.
 */
export async function anhangOeffnen(anhang: Anhang): Promise<string | null> {
  try {
    const antwort = await fetch(anhangUrl(anhang.id), {
      headers: { Authorization: `Bearer ${token() ?? ''}` },
    });
    if (!antwort.ok) throw new Error(String(antwort.status));
    const blob = await antwort.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = anhang.name;
    a.click();
    // Erst freigeben, wenn der Browser den Download übernommen hat.
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return null;
  } catch {
    return 'Die Datei konnte nicht geladen werden.';
  }
}
